import React, { useRef, useState } from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";

interface VideoThumbnailProps {
  src: string;
  size?: "xs" | "small" | "medium";
  className?: string;
}

const SIZE_CONFIG = {
  // NOTE (Kevin, 2025-12-15): xs size (h-5 w-5) is used for chat token inline
  // previews so it lines up with the chat input text.
  xs: {
    container: "h-5 w-5",
    playButton: "h-2.5 w-2.5",
    playIcon: "border-y-[2px] border-l-[3px]",
  },
  small: {
    container: "h-14 w-14",
    playButton: "h-5 w-5",
    playIcon: "border-y-4 border-l-[6px]",
  },
  medium: {
    container: "h-[75px] w-[75px]",
    playButton: "h-6 w-6",
    playIcon: "border-y-6 border-l-[10px]",
  },
};

export const VideoThumbnail = ({
  src,
  size = "small",
  className,
}: VideoThumbnailProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const showPlaceholder = !isVideoLoaded || hasError;

  const handleMouseEnter = () => {
    if (!videoRef.current || showPlaceholder) {
      return;
    }
    void videoRef.current.play();
  };

  const handleMouseLeave = () => {
    if (!videoRef.current) {
      return;
    }
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  };

  const {
    container: sizeClasses,
    playButton: playButtonSize,
    playIcon: playIconSize,
  } = SIZE_CONFIG[size];

  return (
    <div
      className={twMerge(
        "relative shrink-0 overflow-hidden rounded-sm border border-border bg-muted",
        sizeClasses,
        className,
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showPlaceholder && <Skeleton className="h-full w-full" />}
      <video
        ref={videoRef}
        src={src}
        className={twMerge(
          "h-full w-full object-cover",
          showPlaceholder && "hidden",
        )}
        onLoadedData={() => setIsVideoLoaded(true)}
        onError={() => setHasError(true)}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        muted
        playsInline
        loop
      />
      {!showPlaceholder && !isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <div
            className={twMerge(
              "flex items-center justify-center rounded-full bg-white/90",
              playButtonSize,
            )}
          >
            <div
              className={twMerge(
                "ml-0.5 h-0 w-0 border-y-transparent border-l-black",
                playIconSize,
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};
